'use client';

import { getCommissionPercentOptions } from '@svenskaspelab/poolbetting/core/lib';
import type { Commission } from '@svenskaspelab/poolbetting/schema/input';
import { CouponAdditionsCommissionSelector, CouponAdditionsItem } from '@svenskaspelab/poolbetting/ui/CouponAdditions';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ReactNode } from 'react';

import { type InjectedWalletProps, withContextWallet } from '@/components/index.browser';

type CommissionPercentSelectorContainerClientProps = {
  name: string;
  selectedPercent: number;
  commissionPercentOptions: number[];
  commissionPercent: Commission;
};

type CommissionPercentSelectorContainerClientPropsWithInjectedWallet = CommissionPercentSelectorContainerClientProps &
  InjectedWalletProps;

/**
 * Client component responsible for handling the state of the commission percent selector and updating the query parameters accordingly.
 *
 * @param root0 props for the component
 * @param root0.name name of the product for which the commission is being selected, used in the description of the selector
 * @param root0.selectedPercent the currently selected commission percent, used to set the default value of the selector
 * @param root0.commissionPercentOptions list of commission percent options, used to render the options in the selector
 * @param root0.commissionPercent the commission percent for the activated draw, used to determine if some options should be disabled
 * @param root0.contextWallets wallets from context, used to determine what's visible to the user
 * @returns renders the commission percent selector wrapped in a CouponAdditionsItem
 */
export function CommissionPercentSelectorContainerClient({
  name,
  selectedPercent,
  commissionPercentOptions,
  commissionPercent,
  contextWallets,
}: CommissionPercentSelectorContainerClientPropsWithInjectedWallet): ReactNode {
  const router = useRouter();
  const searchParams = useSearchParams();
  let commissionPercentOptionsToUse = commissionPercentOptions;
  if (contextWallets === undefined) {
    return undefined;
  }
  const [firstWallet] = contextWallets;
  if (firstWallet) {
    commissionPercentOptionsToUse = getCommissionPercentOptions({
      maxCommission: 10,
      sharePrice: firstWallet.sharePrice,
      soldShares: firstWallet.soldShares,
    });
  }
  const handleOnChange = (value: number): void => {
    const qs = new URLSearchParams(searchParams.toString());
    qs.set('commission', `${value}`);
    router.replace(`?${qs.toString()}`, { scroll: false });
  };
  return (
    <CouponAdditionsItem title="Välj avgift" description={`Välj avgift för ${name}`}>
      <CouponAdditionsCommissionSelector
        commissionValueOptions={commissionPercentOptionsToUse}
        onValueChange={handleOnChange}
        selectedCommissionPercent={selectedPercent}
        commissionPercent={commissionPercent}
      />
    </CouponAdditionsItem>
  );
}

export default withContextWallet<CommissionPercentSelectorContainerClientProps>(
  CommissionPercentSelectorContainerClient
);
